import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, DollarSign, TrendingUp, Loader2, ShieldCheck, Edit3, X, Users, Briefcase, FileText, Sliders, Search, Download, Check, AlertTriangle, LogOut, Package, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { fmtDateTime } from "@/utils/datetime";
import { planDescription } from "@/utils/planText";
import { TrashDeleteAction } from "@/components/trash/TrashDeleteAction";
import { BulkActionBar, RowCheckbox, SelectAllCheckbox, useBulkSelection, type BulkEntity } from "@/components/bulk/BulkSelection";
import { AccessPolicyEditor, anyEnabledNonCritical, type AccessMap, type ModuleDef } from "@/components/platform/AccessPolicyEditor";
import { PurgeTenantDialog } from "@/components/platform/PurgeTenantDialog";

type Plan = { id?: string; code: string; name: string; price_monthly_minor: number; price_annual_minor: number; currency: string; limits: any; modules: string[]; is_active?: boolean; sort_order?: number; description?: string | null; description_i18n?: Record<string, string> | null };
type Tenant = { id: string; name: string; is_demo: boolean; users: number; active_students: number; subscription: any; deleted_at?: string | null; delete_batch_id?: string | null };
type SignupReq = { id: string; org_name: string; first_name: string; last_name: string; email: string; phone?: string | null; activity_types: string[]; plan_interest?: string | null; message?: string | null; status: "pending" | "approved" | "rejected"; reject_reason?: string | null; activation_url?: string | null; activation_used_at?: string | null; created_at: string; reviewed_at?: string | null };
type Person = { id: string; tenant_id: string; tenant_name: string; first_name: string; last_name: string; email?: string | null; phone?: string | null; person_types: string[]; is_active: boolean; anonymized_at?: string | null; created_at: string; tenant_deleted?: boolean };
type Staff = { user_id: string; email: string; full_name: string; tenant_id: string; tenant_name: string; role_code: string; role_name: string; is_active: boolean };
type InvoiceRow = { id: string; tenant_id: string; tenant_name: string; invoice_number: string; status: string; currency: string; total_minor: number; paid_minor?: number | null; due_date?: string | null; created_at?: string | null };
type FinanceSum = { tenant_id: string; tenant_name: string; invoice_count: number; total_billed_minor: number; total_paid_minor: number; outstanding_minor: number };
type OverrideItem = { module: string; level: "inherit" | "locked" | "view" | "full" };
type OverridesResp = { user: { id: string; email: string; full_name: string; is_platform_owner: boolean }; is_institution_owner: boolean; overrides: OverrideItem[] };
type TenantModuleItem = { module: string; enabled: boolean; in_plan: boolean; in_activity: boolean; effective: boolean; has_override: boolean };

function money(minor: number, currency: string, lang: string) {
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format(minor / 100);
}

// Phase PKG-1: prices are stored as integer minor units (qəpik) — never
// floats. These helpers only exist at the form boundary so users can type
// a normal decimal (50.00) and we still POST an integer to the API.
function minorFromAzn(s: string): number {
    const cleaned = String(s || "").replace(",", ".").trim();
    if (!cleaned) return 0;
    const n = Number.parseFloat(cleaned);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * 100);
}
function aznFromMinor(minor: number | null | undefined): string {
    const n = Math.max(0, Number(minor) || 0);
    return (n / 100).toFixed(2);
}

const BULK_TABS: Record<string, BulkEntity | undefined> = { people: "person", groups: "group", programs: "program", rooms: "room", leads: "lead" };

const MODULES = ["people","groups","schedule","attendance","finance","crm","kindergarten","academics","hr","reports","procurement","inventory","settings"] as const;
const LEVELS: Array<OverrideItem["level"]> = ["inherit","locked","view","full"];

export default function PlatformAdmin() {
    const { t, i18n } = useTranslation();
    const [tab, setTab] = useState<"tenants"|"plans"|"metrics"|"signups"|"people"|"staff"|"groups"|"programs"|"rooms"|"leads"|"finance"|"overrides"|"modules">("tenants");
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [metrics, setMetrics] = useState<any | null>(null);
    const [signups, setSignups] = useState<SignupReq[]>([]);
    const [signupFilter, setSignupFilter] = useState<"pending"|"approved"|"rejected"|"all">("pending");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [editSubFor, setEditSubFor] = useState<Tenant | null>(null);
    // Phase TD-1 — tenant soft-delete flow.
    const [deleteTenantFor, setDeleteTenantFor] = useState<Tenant | null>(null);
    const [purgeFor, setPurgeFor] = useState<Tenant | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deletedTenants, setDeletedTenants] = useState<Tenant[]>([]);
    const [showDeletedTab, setShowDeletedTab] = useState(false);
    // Phase PKG-1: plan editor + tenant custom pricing dialogs.
    const [editPlanFor, setEditPlanFor] = useState<Plan | null>(null);
    const [pricingFor, setPricingFor] = useState<Tenant | null>(null);
    const [deactivatePlanFor, setDeactivatePlanFor] = useState<Plan | null>(null);
    const [deactivateBusy, setDeactivateBusy] = useState(false);
    const [rejectFor, setRejectFor] = useState<SignupReq | null>(null);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectBusy, setRejectBusy] = useState(false);

    // Cross-tenant list state
    const [tenantFilter, setTenantFilter] = useState<string>("");
    const [q, setQ] = useState("");
    const [people, setPeople] = useState<Person[]>([]);
    const [peopleTotal, setPeopleTotal] = useState(0);
    const [staff, setStaff] = useState<Staff[]>([]);
    const [staffTotal, setStaffTotal] = useState(0);
    const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
    const [invoicesTotal, setInvoicesTotal] = useState(0);
    // Phase TD-2 — cross-tenant work-data lists (platform owner).
    type RowBase = { id: string; tenant_id: string; tenant_name?: string | null; tenant_deleted?: boolean; is_active?: boolean };
    type GroupRow = RowBase & { name: string; status?: string };
    type ProgRow  = RowBase & { name: string; code?: string | null; activity_type?: string | null };
    type RoomRow  = RowBase & { name: string; capacity?: number | null };
    type LeadRow  = RowBase & { contact_name: string; phone?: string | null; email?: string | null };
    const [groups, setGroups] = useState<GroupRow[]>([]); const [groupsTotal, setGroupsTotal] = useState(0);
    const [programs, setPrograms] = useState<ProgRow[]>([]); const [programsTotal, setProgramsTotal] = useState(0);
    const [rooms, setRooms] = useState<RoomRow[]>([]); const [roomsTotal, setRoomsTotal] = useState(0);
    const [leads, setLeads] = useState<LeadRow[]>([]); const [leadsTotal, setLeadsTotal] = useState(0);
    const [finance, setFinance] = useState<{ items: FinanceSum[]; totals: any } | null>(null);
    const [page, setPage] = useState(0);
    const pageSize = 50;
    const [reloadKey, setReloadKey] = useState(0);
    const [showDeletedTenants, setShowDeletedTenants] = useState(false);
    const bulk = useBulkSelection(`${tab}|${tenantFilter}|${q}`);

    // Overrides state
    const [overrideUser, setOverrideUser] = useState<Staff | null>(null);
    const [overridesData, setOverridesData] = useState<OverridesResp | null>(null);
    const [overrideBusy, setOverrideBusy] = useState(false);

    // Modules tab (Phase TAC-1) — hierarchical access policy per tenant.
    const [moduleTenantId, setModuleTenantId] = useState<string>("");
    const [accessCatalog, setAccessCatalog] = useState<ModuleDef[]>([]);
    const [accessPolicy, setAccessPolicy] = useState<AccessMap>({});
    const [accessLoaded, setAccessLoaded] = useState(false);
    const [modulesBusy, setModulesBusy] = useState(false);
    // Signup approve dialog policy state (Phase TAC-1).
    const [approveFor, setApproveFor] = useState<SignupReq | null>(null);
    const [approvePolicy, setApprovePolicy] = useState<AccessMap>({});
    const [approveBusy, setApproveBusy] = useState(false);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const [ts, ps, m, sr, tsAll] = await Promise.all([
                api.get("/platform/tenants"), api.get("/platform/plans"), api.get("/platform/metrics"),
                api.get("/platform/signup-requests", { params: { status: signupFilter } }),
                api.get("/platform/tenants", { params: { include_deleted: true } }),
            ]);
            setTenants(ts.data.items || []); setPlans(ps.data.items || []); setMetrics(m.data);
            setSignups(sr.data.items || []);
            setDeletedTenants((tsAll.data.items || []).filter((x: Tenant) => x.deleted_at));
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [signupFilter]);

    // Load cross-tenant lists whenever the active tab or its filters change
    useEffect(() => {
        const params: any = { limit: pageSize, offset: page * pageSize };
        if (tenantFilter) params.tenant_id = tenantFilter;
        if (q.trim()) params.q = q.trim();
        if (showDeletedTenants && BULK_TABS[tab]) params.include_deleted_tenants = true;
        if (tab === "people") api.get("/platform/people", { params }).then(r => { setPeople(r.data.items); setPeopleTotal(r.data.total); }).catch(e => toast.error(formatApiError(e)));
        else if (tab === "staff") api.get("/platform/staff", { params }).then(r => { setStaff(r.data.items); setStaffTotal(r.data.total); }).catch(e => toast.error(formatApiError(e)));
        else if (tab === "groups") api.get("/platform/groups", { params }).then(r => { setGroups(r.data.items); setGroupsTotal(r.data.total); }).catch(e => toast.error(formatApiError(e)));
        else if (tab === "programs") api.get("/platform/programs", { params }).then(r => { setPrograms(r.data.items); setProgramsTotal(r.data.total); }).catch(e => toast.error(formatApiError(e)));
        else if (tab === "rooms") api.get("/platform/rooms", { params }).then(r => { setRooms(r.data.items); setRoomsTotal(r.data.total); }).catch(e => toast.error(formatApiError(e)));
        else if (tab === "leads") api.get("/platform/leads", { params }).then(r => { setLeads(r.data.items); setLeadsTotal(r.data.total); }).catch(e => toast.error(formatApiError(e)));
        else if (tab === "finance") {
            api.get("/platform/invoices", { params }).then(r => { setInvoices(r.data.items); setInvoicesTotal(r.data.total); }).catch(e => toast.error(formatApiError(e)));
            api.get("/platform/finance/summary").then(r => setFinance(r.data)).catch(e => toast.error(formatApiError(e)));
        }
    }, [tab, tenantFilter, q, page, reloadKey, showDeletedTenants]);

    const approve = async (s: SignupReq) => {
        // Phase TAC-1: platform owner MUST hand-pick modules for the new
        // tenant. Load the catalog with sensible defaults, then open the
        // approve dialog which POSTs {access_policy} on confirm.
        try {
            const r = await api.get("/platform/access-catalog");
            const cat: ModuleDef[] = r.data.modules || [];
            // Seed policy: enable view for all non-critical modules by default;
            // platform owner then trims down.
            const seed: AccessMap = {};
            for (const m of cat) {
                seed[m.code] = Object.fromEntries(m.actions.map(a => [a, !m.account_critical]));
            }
            setAccessCatalog(cat);
            setApprovePolicy(seed);
            setApproveFor(s);
        } catch (e: any) { toast.error(formatApiError(e)); }
    };

    const doApprove = async () => {
        if (!approveFor) return;
        if (!anyEnabledNonCritical(approvePolicy, accessCatalog)) {
            toast.error(t("platform.access.min_one")); return;
        }
        setApproveBusy(true);
        try {
            const r = await api.post(`/platform/signup-requests/${approveFor.id}/approve`,
                { access_policy: approvePolicy });
            const em = r.data?.email;
            if (em?.status === "sent") toast.success(t("platform.signup.email_sent", { email: approveFor.email }));
            else toast.warning(t("platform.signup.email_failed", { error: em?.error || em?.status || "—" }), { duration: 12000 });
            setApproveFor(null);
            await load();
        } catch (e: any) { toast.error(formatApiError(e)); }
        finally { setApproveBusy(false); }
    };
    const doReject = async () => {
        if (!rejectFor) return;
        if (rejectReason.trim().length < 2) { toast.error(t("platform.signup.reject_reason_required")); return; }
        setRejectBusy(true);
        try { await api.post(`/platform/signup-requests/${rejectFor.id}/reject`, { reason: rejectReason.trim() }); setRejectFor(null); setRejectReason(""); await load(); toast.success(t("platform.signup.reject_confirm")); }
        catch (e: any) { toast.error(formatApiError(e)); }
        finally { setRejectBusy(false); }
    };
    const copy = async (s: string) => { try { await navigator.clipboard.writeText(s); toast.success(t("common.copied")); } catch {} };
    const pendingCount = signups.filter(s => s.status === "pending").length;

    // Phase PKG-1: (de)activate a plan without opening the full editor.
    // - Deactivate uses DELETE which auto-downgrades to `is_active=false`
    //   when the plan is referenced by any subscription (never a hard delete
    //   for in-use plans, per backend contract).
    // - Activate reuses PATCH with apply_scope=new_only so existing
    //   subscriptions are not silently mutated.
    const doPlanDeactivate = async (p: Plan) => {
        setDeactivateBusy(true);
        try {
            await api.delete(`/platform/plans/${p.id}`);
            toast.success(t("platform.plans.deactivated_toast"));
            setDeactivatePlanFor(null);
            await load();
        } catch (e: any) { toast.error(formatApiError(e)); }
        finally { setDeactivateBusy(false); }
    };
    const doPlanActivate = async (p: Plan) => {
        try {
            await api.patch(`/platform/plans/${p.id}`, {
                code: p.code, name: p.name, description: p.description,
                description_i18n: p.description_i18n || undefined,
                price_monthly_minor: p.price_monthly_minor,
                price_annual_minor: p.price_annual_minor,
                currency: p.currency, limits: p.limits || {},
                modules: p.modules || [], is_active: true,
                sort_order: p.sort_order ?? 100,
                apply_scope: "new_only",
            });
            toast.success(t("platform.plans.activated_toast"));
            await load();
        } catch (e: any) { toast.error(formatApiError(e)); }
    };

    // Phase 2 (F-5): global search deep-link — /platform?tab=groups&q=Name&tenant_id=…
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const qtab = searchParams.get("tab");
        if (!qtab || !["people", "groups", "programs", "rooms", "leads"].includes(qtab)) return;
        setTab(qtab as any);
        setTenantFilter(searchParams.get("tenant_id") || "");
        setQ(searchParams.get("q") || "");
    }, [searchParams]);

    // Reset paging when tab or filters change
    useEffect(() => { setPage(0); }, [tab, tenantFilter, q]);

    // Load overrides when a user is selected
    useEffect(() => {
        if (!overrideUser) { setOverridesData(null); return; }
        api.get(`/platform/overrides/${overrideUser.user_id}`).then(r => setOverridesData(r.data)).catch(e => toast.error(formatApiError(e)));
    }, [overrideUser?.user_id]);

    // Load access policy for the selected tenant (Phase TAC-1).
    useEffect(() => {
        if (!moduleTenantId) { setAccessCatalog([]); setAccessPolicy({}); setAccessLoaded(false); return; }
        setAccessLoaded(false);
        api.get(`/platform/tenants/${moduleTenantId}/access-policy`)
            .then(r => {
                setAccessCatalog(r.data.modules || []);
                setAccessPolicy(r.data.effective || {});
                setAccessLoaded(true);
            })
            .catch(e => { toast.error(formatApiError(e)); setAccessLoaded(true); });
    }, [moduleTenantId]);

    const saveAccessPolicy = async () => {
        if (!moduleTenantId) return;
        if (!anyEnabledNonCritical(accessPolicy, accessCatalog)) {
            toast.error(t("platform.access.min_one")); return;
        }
        setModulesBusy(true);
        try {
            const r = await api.put(`/platform/tenants/${moduleTenantId}/access-policy`,
                { policy: accessPolicy, require_min_one: true });
            setAccessPolicy(r.data.effective || accessPolicy);
            toast.success(t("platform.access.saved"));
        } catch (e: any) { toast.error(formatApiError(e)); }
        finally { setModulesBusy(false); }
    };

    const toggleModule = (_m: string) => { /* replaced by AccessPolicyEditor */ };
    const saveModules = saveAccessPolicy;

    const setOverrideLevel = (module: string, level: OverrideItem["level"]) => {
        if (!overridesData) return;
        if (overridesData.is_institution_owner && level === "locked") {
            toast.warning(t("platform.override.io_floor"));
            level = "view";
        }
        setOverridesData({ ...overridesData, overrides: overridesData.overrides.map(o => o.module === module ? { ...o, level } : o) });
    };
    const saveOverrides = async () => {
        if (!overridesData || !overrideUser) return;
        setOverrideBusy(true);
        try {
            await api.put(`/platform/overrides/${overrideUser.user_id}`, { overrides: overridesData.overrides });
            toast.success(t("platform.override.saved"));
        } catch (e: any) {
            const raw = formatApiError(e);
            toast.error(raw === "cannot_override_self" ? t("platform.override.cannot_self") : raw);
        } finally { setOverrideBusy(false); }
    };
    const impersonate = async (s: Staff) => {
        if (!confirm(t("platform.impersonate.confirm", { name: s.full_name || s.email }))) return;
        try {
            await api.post("/platform/impersonate", { user_id: s.user_id, duration_minutes: 30 });
            (window as any).__eduerp_impersonation__ = { email: s.email, full_name: s.full_name };
            toast.success(t("platform.impersonate.started"));
            setTimeout(() => window.location.replace("/dashboard"), 400);
        } catch (e: any) { toast.error(formatApiError(e)); }
    };
    const exportCsv = (kind: "people"|"staff"|"invoices"|"leads") => {
        const params = new URLSearchParams({ format: "csv", limit: "500" });
        if (tenantFilter) params.set("tenant_id", tenantFilter);
        if (q.trim()) params.set("q", q.trim());
        if (showDeletedTenants) params.set("include_deleted_tenants", "true");
        const base = (import.meta as any).env?.VITE_BACKEND_URL || (window as any).REACT_APP_BACKEND_URL || "";
        const url = (process.env.REACT_APP_BACKEND_URL || base) + `/api/platform/${kind}?` + params.toString();
        window.open(url, "_blank");
    };

    const tenantOpts = useMemo(() => [{ id: "", name: t("common.all") }, ...tenants.map(x => ({ id: x.id, name: x.name }))], [tenants, t]);

    return (
        <div className="space-y-4" data-testid="platform-admin">
            <div className="flex items-center gap-2">
                <ShieldCheck size={20} style={{ color: "var(--primary)" }} />
                <h1 className="text-2xl font-semibold flex-1">{t("platform.title")}</h1>
            </div>
            <div className="flex gap-1 flex-wrap">
                {(["tenants","plans","metrics","signups","people","staff","groups","programs","rooms","leads","finance","overrides","modules"] as const).map(k => (
                    <button key={k} data-testid={`platform-tab-${k}`} onClick={() => setTab(k)}
                        className="rounded-md px-3 py-1.5 text-sm inline-flex items-center gap-1.5"
                        style={{ background: tab === k ? "var(--selected-bg)" : "transparent", color: tab === k ? "var(--text)" : "var(--text-muted)" }}>
                        {k === "people" && <Users size={14} />}
                        {k === "staff" && <Briefcase size={14} />}
                        {k === "finance" && <FileText size={14} />}
                        {k === "overrides" && <Sliders size={14} />}
                        {k === "modules" && <Package size={14} />}
                        {t(`platform.tab.${k}`)}
                        {k === "signups" && pendingCount > 0 && (
                            <span className="text-[10px] rounded-full px-1.5 py-0.5" data-testid="signup-pending-badge"
                                style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>{pendingCount}</span>
                        )}
                    </button>
                ))}
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}

            {/* Filter bar for cross-tenant tabs */}
            {["people","staff","groups","programs","rooms","leads","finance"].includes(tab) && (
                <div className="flex flex-wrap gap-2 items-center rounded-xl border p-3" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    <select data-testid="platform-tenant-filter" value={tenantFilter} onChange={e => setTenantFilter(e.target.value)}
                        className="rounded-md border px-2 py-1.5 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                        {tenantOpts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <div className="relative flex-1 min-w-[180px]">
                        <Search size={14} className="absolute left-2 top-2.5" style={{ color: "var(--text-muted)" }} />
                        <input data-testid="platform-search" value={q} onChange={e => setQ(e.target.value)} placeholder={t("common.search")}
                            className="rounded-md border pl-7 pr-2 py-1.5 text-sm w-full" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                    </div>
                    {BULK_TABS[tab] && (
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--text-muted)" }}>
                            <input type="checkbox" data-testid="platform-show-deleted-tenants" checked={showDeletedTenants}
                                onChange={e => setShowDeletedTenants(e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" />
                            {t("platform.show_deleted_tenants")}
                        </label>
                    )}
                    {tab !== "finance" && (
                        <button onClick={() => exportCsv(tab as any)} data-testid="platform-export-csv"
                            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm"
                            style={{ borderColor: "var(--border)", background: "var(--input)", color: "var(--text)" }}>
                            <Download size={14} />{t("common.export_csv")}
                        </button>
                    )}
                </div>
            )}

            {loading && tab !== "people" && tab !== "staff" && tab !== "finance" && tab !== "overrides" && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}

            {/* --- People --- */}
            {tab === "people" && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-people">
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="px-3 py-2 w-8"><SelectAllCheckbox selection={bulk} rows={people} testid="platform-person-select-all" /></th>
                                <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.name")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.email")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.phone")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.types")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.active")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {people.map(p => (
                                <tr key={p.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-person-${p.id}`}>
                                    <td className="px-3 py-2"><RowCheckbox selection={bulk} item={p} testid={`platform-person-select-${p.id}`} /></td>
                                    <td className="px-3 py-2"><TenantNameCell row={p} /></td>
                                    <td className="px-3 py-2">
                                        {p.first_name} {p.last_name}
                                        {p.anonymized_at && <span className="ml-2 text-[10px] rounded-full px-1.5 py-0.5" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>{t("platform.people.anonymized")}</span>}
                                    </td>
                                    <td className="px-3 py-2">{p.email || "—"}</td>
                                    <td className="px-3 py-2">{p.phone || "—"}</td>
                                    <td className="px-3 py-2">{p.person_types.join(", ")}</td>
                                    <td className="px-3 py-2">{p.is_active ? <Check size={14} style={{ color: "var(--success-fg)" }} /> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                                    <td className="px-3 py-2 text-right">
                                        <TrashDeleteAction entityType="person" entityId={p.id}
                                            entityLabel={`${p.first_name} ${p.last_name}`}
                                            targetTenantId={p.tenant_id}
                                            onArchived={() => setReloadKey(k => k + 1)}
                                            dataTestidPrefix={`platform-person-del-${p.id}`} />
                                    </td>
                                </tr>
                            ))}
                            {people.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.no_data")}</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} setPage={setPage} total={peopleTotal} pageSize={pageSize} t={t} />
                </div>
            )}

            {/* --- Groups (TD-2) --- */}
            {tab === "groups" && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-groups">
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="px-3 py-2 w-8"><SelectAllCheckbox selection={bulk} rows={groups} testid="platform-group-select-all" /></th>
                                <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                <th className="text-left px-3 py-2">{t("platform.groups.name")}</th>
                                <th className="text-left px-3 py-2">{t("platform.groups.status")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.active")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map(g => (
                                <tr key={g.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-group-${g.id}`}>
                                    <td className="px-3 py-2"><RowCheckbox selection={bulk} item={g} testid={`platform-group-select-${g.id}`} /></td>
                                    <td className="px-3 py-2"><TenantNameCell row={g} /></td>
                                    <td className="px-3 py-2">{g.name}</td>
                                    <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{g.status || "—"}</td>
                                    <td className="px-3 py-2">{g.is_active ? <Check size={14} style={{ color: "var(--success-fg)" }} /> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                                    <td className="px-3 py-2 text-right">
                                        <TrashDeleteAction entityType="group" entityId={g.id} entityLabel={g.name}
                                            targetTenantId={g.tenant_id}
                                            onArchived={() => setReloadKey(k => k + 1)}
                                            dataTestidPrefix={`platform-group-del-${g.id}`} />
                                    </td>
                                </tr>
                            ))}
                            {groups.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.no_data")}</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} setPage={setPage} total={groupsTotal} pageSize={pageSize} t={t} />
                </div>
            )}

            {/* --- Programs (TD-2) --- */}
            {tab === "programs" && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-programs">
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="px-3 py-2 w-8"><SelectAllCheckbox selection={bulk} rows={programs} testid="platform-program-select-all" /></th>
                                <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                <th className="text-left px-3 py-2">{t("platform.programs.name")}</th>
                                <th className="text-left px-3 py-2">{t("platform.programs.code")}</th>
                                <th className="text-left px-3 py-2">{t("platform.programs.activity_type")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.active")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {programs.map(pr => (
                                <tr key={pr.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-program-${pr.id}`}>
                                    <td className="px-3 py-2"><RowCheckbox selection={bulk} item={pr} testid={`platform-program-select-${pr.id}`} /></td>
                                    <td className="px-3 py-2"><TenantNameCell row={pr} /></td>
                                    <td className="px-3 py-2">{pr.name}</td>
                                    <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{pr.code || "—"}</td>
                                    <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{pr.activity_type || "—"}</td>
                                    <td className="px-3 py-2">{pr.is_active ? <Check size={14} style={{ color: "var(--success-fg)" }} /> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                                    <td className="px-3 py-2 text-right">
                                        <TrashDeleteAction entityType="program" entityId={pr.id} entityLabel={pr.name}
                                            targetTenantId={pr.tenant_id}
                                            onArchived={() => setReloadKey(k => k + 1)}
                                            dataTestidPrefix={`platform-program-del-${pr.id}`} />
                                    </td>
                                </tr>
                            ))}
                            {programs.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.no_data")}</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} setPage={setPage} total={programsTotal} pageSize={pageSize} t={t} />
                </div>
            )}

            {/* --- Rooms (TD-2) --- */}
            {tab === "rooms" && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-rooms">
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="px-3 py-2 w-8"><SelectAllCheckbox selection={bulk} rows={rooms} testid="platform-room-select-all" /></th>
                                <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                <th className="text-left px-3 py-2">{t("platform.rooms.name")}</th>
                                <th className="text-left px-3 py-2">{t("platform.rooms.capacity")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.active")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rooms.map(rm => (
                                <tr key={rm.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-room-${rm.id}`}>
                                    <td className="px-3 py-2"><RowCheckbox selection={bulk} item={rm} testid={`platform-room-select-${rm.id}`} /></td>
                                    <td className="px-3 py-2"><TenantNameCell row={rm} /></td>
                                    <td className="px-3 py-2">{rm.name}</td>
                                    <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{rm.capacity ?? "—"}</td>
                                    <td className="px-3 py-2">{rm.is_active ? <Check size={14} style={{ color: "var(--success-fg)" }} /> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                                    <td className="px-3 py-2 text-right">
                                        <TrashDeleteAction entityType="room" entityId={rm.id} entityLabel={rm.name}
                                            targetTenantId={rm.tenant_id}
                                            onArchived={() => setReloadKey(k => k + 1)}
                                            dataTestidPrefix={`platform-room-del-${rm.id}`} />
                                    </td>
                                </tr>
                            ))}
                            {rooms.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.no_data")}</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} setPage={setPage} total={roomsTotal} pageSize={pageSize} t={t} />
                </div>
            )}

            {/* --- Leads (TD-2) --- */}
            {tab === "leads" && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-leads">
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="px-3 py-2 w-8"><SelectAllCheckbox selection={bulk} rows={leads} testid="platform-lead-select-all" /></th>
                                <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                <th className="text-left px-3 py-2">{t("platform.leads.contact")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.email")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.phone")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map(ld => (
                                <tr key={ld.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-lead-${ld.id}`}>
                                    <td className="px-3 py-2"><RowCheckbox selection={bulk} item={ld} testid={`platform-lead-select-${ld.id}`} /></td>
                                    <td className="px-3 py-2"><TenantNameCell row={ld} /></td>
                                    <td className="px-3 py-2">{ld.contact_name || "—"}</td>
                                    <td className="px-3 py-2">{ld.email || "—"}</td>
                                    <td className="px-3 py-2">{ld.phone || "—"}</td>
                                    <td className="px-3 py-2 text-right">
                                        <TrashDeleteAction entityType="lead" entityId={ld.id} entityLabel={ld.contact_name || ld.email || ld.phone || ld.id}
                                            targetTenantId={ld.tenant_id}
                                            onArchived={() => setReloadKey(k => k + 1)}
                                            dataTestidPrefix={`platform-lead-del-${ld.id}`} />
                                    </td>
                                </tr>
                            ))}
                            {leads.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.no_data")}</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} setPage={setPage} total={leadsTotal} pageSize={pageSize} t={t} />
                </div>
            )}

            {BULK_TABS[tab] && (
                <BulkActionBar selection={bulk} entityType={BULK_TABS[tab] as BulkEntity} platform
                    onDone={() => setReloadKey(k => k + 1)} testidPrefix="platform-bulk" />
            )}

            {/* --- Staff --- */}
            {tab === "staff" && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-staff">
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.name")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.email")}</th>
                                <th className="text-left px-3 py-2">{t("platform.staff.role")}</th>
                                <th className="text-left px-3 py-2">{t("platform.people.active")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {staff.map(s => (
                                <tr key={s.user_id + s.tenant_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-staff-${s.user_id}`}>
                                    <td className="px-3 py-2">{s.tenant_name}</td>
                                    <td className="px-3 py-2">{s.full_name}</td>
                                    <td className="px-3 py-2">{s.email}</td>
                                    <td className="px-3 py-2"><span className="text-xs rounded-full px-2 py-0.5" style={{ background: "var(--input)" }}>{s.role_code}</span></td>
                                    <td className="px-3 py-2">{s.is_active ? <Check size={14} style={{ color: "var(--success-fg)" }} /> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button data-testid={`staff-impersonate-${s.user_id}`} onClick={() => impersonate(s)}
                                            className="inline-flex items-center gap-1 text-xs mr-3" style={{ color: "var(--warning-fg)" }}>
                                            <LogOut size={12} />{t("platform.impersonate.button")}
                                        </button>
                                        <button data-testid={`staff-override-${s.user_id}`} onClick={() => { setOverrideUser(s); setTab("overrides"); }}
                                            className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--primary)" }}>
                                            <Sliders size={12} />{t("platform.tab.overrides")}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {staff.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.no_data")}</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} setPage={setPage} total={staffTotal} pageSize={pageSize} t={t} />
                </div>
            )}

            {/* --- Finance --- */}
            {tab === "finance" && (
                <div className="space-y-3" data-testid="platform-finance">
                    {finance && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <MetricCard icon={<FileText size={16} />} label={t("platform.invoices.number") + "s"} value={String(finance.totals.invoice_count)} />
                            <MetricCard icon={<DollarSign size={16} />} label={t("platform.finance.billed")} value={money(finance.totals.total_billed, "AZN", i18n.language)} />
                            <MetricCard icon={<Check size={16} />} label={t("platform.finance.paid")} value={money(finance.totals.total_paid, "AZN", i18n.language)} />
                            <MetricCard icon={<AlertTriangle size={16} />} label={t("platform.finance.outstanding")} value={money(finance.totals.outstanding_minor, "AZN", i18n.language)} />
                        </div>
                    )}
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <div className="flex items-center justify-between px-3 py-2" style={{ background: "var(--input)" }}>
                            <div className="text-xs font-medium">{t("platform.finance.summary")}</div>
                            <button onClick={() => exportCsv("invoices")} data-testid="finance-export-csv" className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--primary)" }}>
                                <Download size={12} />{t("common.export_csv")}
                            </button>
                        </div>
                        <table className="w-full text-sm">
                            <thead style={{ background: "var(--input)" }}>
                                <tr>
                                    <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                    <th className="text-right px-3 py-2">#</th>
                                    <th className="text-right px-3 py-2">{t("platform.finance.billed")}</th>
                                    <th className="text-right px-3 py-2">{t("platform.finance.paid")}</th>
                                    <th className="text-right px-3 py-2">{t("platform.finance.outstanding")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {finance?.items.map(row => (
                                    <tr key={row.tenant_id} className="border-t" style={{ borderColor: "var(--border)" }}>
                                        <td className="px-3 py-2">{row.tenant_name}</td>
                                        <td className="px-3 py-2 text-right">{row.invoice_count}</td>
                                        <td className="px-3 py-2 text-right">{money(row.total_billed_minor, "AZN", i18n.language)}</td>
                                        <td className="px-3 py-2 text-right">{money(row.total_paid_minor, "AZN", i18n.language)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{money(row.outstanding_minor, "AZN", i18n.language)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <table className="w-full text-sm">
                            <thead style={{ background: "var(--input)" }}>
                                <tr>
                                    <th className="text-left px-3 py-2">{t("platform.people.tenant")}</th>
                                    <th className="text-left px-3 py-2">{t("platform.invoices.number")}</th>
                                    <th className="text-left px-3 py-2">{t("platform.invoices.status")}</th>
                                    <th className="text-right px-3 py-2">{t("platform.invoices.total")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map(inv => (
                                    <tr key={inv.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-invoice-${inv.id}`}>
                                        <td className="px-3 py-2">{inv.tenant_name}</td>
                                        <td className="px-3 py-2">{inv.invoice_number}</td>
                                        <td className="px-3 py-2"><InvoiceStatus s={inv.status} t={t} /></td>
                                        <td className="px-3 py-2 text-right">{money(inv.total_minor, inv.currency, i18n.language)}</td>
                                    </tr>
                                ))}
                                {invoices.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.no_data")}</td></tr>}
                            </tbody>
                        </table>
                        <Pagination page={page} setPage={setPage} total={invoicesTotal} pageSize={pageSize} t={t} />
                    </div>
                </div>
            )}

            {/* --- Overrides --- */}
            {tab === "overrides" && (
                <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-overrides">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-lg font-semibold">{t("platform.override.title")}</h2>
                        {overrideUser && <span className="text-sm" style={{ color: "var(--text-muted)" }}>· {overrideUser.full_name} ({overrideUser.email})</span>}
                    </div>
                    {!overrideUser && <div className="mt-3 rounded-md p-4 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)" }} data-testid="override-empty">{t("platform.override.select_user")}</div>}
                    {overrideUser && overridesData && (
                        <div className="mt-4">
                            {overridesData.is_institution_owner && (
                                <div className="mb-3 rounded-md p-2 text-xs inline-flex items-center gap-1.5" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="override-io-floor">
                                    <AlertTriangle size={12} />{t("platform.override.io_floor")}
                                </div>
                            )}
                            <div className="overflow-hidden rounded-md border" style={{ borderColor: "var(--border)" }}>
                                <table className="w-full text-sm">
                                    <thead style={{ background: "var(--input)" }}>
                                        <tr>
                                            <th className="text-left px-3 py-2">{t("platform.override.module")}</th>
                                            {LEVELS.map(l => <th key={l} className="text-center px-3 py-2">{t(`platform.override.level.${l}`)}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {MODULES.map(m => {
                                            const cur = overridesData.overrides.find(o => o.module === m)?.level || "inherit";
                                            return (
                                                <tr key={m} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`override-row-${m}`}>
                                                    <td className="px-3 py-2 font-medium">{t(`nav.${m}`, m)}</td>
                                                    {LEVELS.map(l => (
                                                        <td key={l} className="px-3 py-2 text-center">
                                                            <input type="radio" data-testid={`override-${m}-${l}`}
                                                                name={`ov-${m}`} checked={cur === l}
                                                                onChange={() => setOverrideLevel(m, l)}
                                                                disabled={overridesData.is_institution_owner && l === "locked"} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <button data-testid="override-save" onClick={saveOverrides} disabled={overrideBusy}
                                    className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                                    style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                                    {overrideBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}{t("platform.override.save")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* --- Modules (per-tenant access policy, Phase TAC-1) --- */}
            {tab === "modules" && (
                <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-modules">
                    <div className="flex items-center gap-3 flex-wrap mb-3">
                        <h2 className="text-lg font-semibold">{t("platform.access.title")}</h2>
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("platform.access.desc")}</p>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <label className="text-xs" style={{ color: "var(--text-muted)" }}>{t("platform.access.select_tenant")}:</label>
                        <select data-testid="modules-tenant-select" value={moduleTenantId} onChange={e => setModuleTenantId(e.target.value)}
                            className="rounded-md border px-2 py-1.5 text-sm min-w-[240px]"
                            style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                            <option value="">{t("platform.access.pick_first")}</option>
                            {tenants.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                        </select>
                    </div>
                    {!moduleTenantId && <div className="rounded-md p-4 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)" }} data-testid="modules-empty">{t("platform.access.pick_first")}</div>}
                    {moduleTenantId && !accessLoaded && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
                    {moduleTenantId && accessLoaded && (
                        <>
                            <AccessPolicyEditor modules={accessCatalog} value={accessPolicy}
                                onChange={setAccessPolicy} testIdPrefix="policy" />
                            <div className="mt-4 flex justify-end">
                                <button data-testid="modules-save" onClick={saveAccessPolicy} disabled={modulesBusy}
                                    className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                                    style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                                    {modulesBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                    {t("platform.access.save")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {tab === "metrics" && metrics && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="platform-metrics">
                    <MetricCard icon={<Building2 size={16} />} label={t("platform.metric.tenants")} value={String(metrics.tenants_total)} />
                    <MetricCard icon={<DollarSign size={16} />} label={t("platform.metric.mrr")} value={money(metrics.mrr_minor, "AZN", i18n.language)} />
                    <MetricCard icon={<TrendingUp size={16} />} label={t("platform.metric.conv")} value={`${metrics.trial_to_paid_pct}%`} />
                    <MetricCard icon={<ShieldCheck size={16} />} label={t("platform.metric.active")} value={String(metrics.by_status?.active || 0)} />
                    <div className="rounded-xl border p-4 col-span-2 lg:col-span-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("platform.metric.breakdown")}</div>
                        <div className="flex flex-wrap gap-4 text-sm">
                            {Object.entries(metrics.by_status || {}).map(([k, v]) => (<div key={k}>{t(`sub.status.${k}`, k)}: <span className="font-semibold">{v as any}</span></div>))}
                        </div>
                    </div>
                </div>
            )}

            {tab === "tenants" && !loading && (
                <>
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="text-left px-3 py-2">{t("platform.tenants.name")}</th>
                                <th className="text-left px-3 py-2">{t("platform.tenants.plan")}</th>
                                <th className="text-left px-3 py-2">{t("common.status")}</th>
                                <th className="text-left px-3 py-2">{t("platform.tenants.users")}</th>
                                <th className="text-left px-3 py-2">{t("platform.tenants.students")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.map(t2 => (
                                <tr key={t2.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-tenant-${t2.id}`}>
                                    <td className="px-3 py-2">{t2.name}{t2.is_demo && <span className="ml-2 text-xs" style={{ color: "var(--text-muted)" }}>demo</span>}</td>
                                    <td className="px-3 py-2">{t2.subscription?.plan?.name || "—"}</td>
                                    <td className="px-3 py-2"><StatusBadge status={t2.subscription?.status} t={t} /></td>
                                    <td className="px-3 py-2">{t2.users}</td>
                                    <td className="px-3 py-2">{t2.active_students}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={() => setPricingFor(t2)} data-testid={`platform-pricing-${t2.id}`}
                                            className="inline-flex items-center gap-1 text-xs mr-3" style={{ color: "var(--accent)" }}>
                                            <DollarSign size={12} />{t("platform.pricing.set_button")}
                                        </button>
                                        <button onClick={() => setEditSubFor(t2)} data-testid={`platform-edit-${t2.id}`} className="inline-flex items-center gap-1 text-xs mr-3" style={{ color: "var(--primary)" }}><Edit3 size={12} />{t("platform.edit_sub")}</button>
                                        <button onClick={() => setDeleteTenantFor(t2)} data-testid={`platform-delete-${t2.id}`}
                                            className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--error-fg)" }}>
                                            <X size={12} />{t("platform.tenants.delete_button")}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {tenants.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</td></tr>}
                        </tbody>
                    </table>
                </div>
                {deletedTenants.length > 0 && (
                    <div className="rounded-xl border overflow-hidden mt-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="platform-tenants-trash">
                        <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: "var(--border)", background: "var(--input)" }}>
                            <AlertTriangle size={14} style={{ color: "var(--warning-fg)" }} />
                            <div className="text-sm font-semibold">{t("platform.tenants.trash_title")}</div>
                            <span className="text-xs" style={{ color: "var(--text-muted)" }}>{deletedTenants.length}</span>
                        </div>
                        <table className="w-full text-sm">
                            <tbody>
                                {deletedTenants.map(t2 => (
                                    <tr key={t2.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`platform-tenant-trash-${t2.id}`}>
                                        <td className="px-3 py-2" style={{ color: "var(--text)" }}>{t2.name}</td>
                                        <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{t2.deleted_at ? fmtDateTime(t2.deleted_at) : ""}</td>
                                        <td className="px-3 py-2 text-right">
                                            <button data-testid={`platform-restore-${t2.id}`}
                                                onClick={async () => {
                                                    try {
                                                        await api.post(`/platform/tenants/${t2.id}/restore`);
                                                        toast.success(t("platform.tenants.restore_success"));
                                                        await load();
                                                    } catch (e: any) {
                                                        toast.error(formatApiError(e));
                                                    }
                                                }}
                                                className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--success-fg)" }}>
                                                <Check size={12} />{t("platform.tenants.restore_button")}
                                            </button>
                                            <button data-testid={`tenant-hard-delete-${t2.id}`} onClick={() => setPurgeFor(t2)}
                                                className="inline-flex items-center gap-1 text-xs ml-3 font-medium" style={{ color: "var(--error-fg)" }}>
                                                <Trash2 size={12} />{t("platform.tenants.purge_button")}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </>
            )}

            {tab === "plans" && !loading && (
                <div className="grid gap-3 md:grid-cols-3" data-testid="platform-plans">
                    {plans.map(p => (
                        <div key={p.id || p.code} className="rounded-xl border p-4 flex flex-col gap-2" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid={`platform-plan-${p.id || p.code}`}>
                            <div className="flex items-center justify-between">
                                <div className="text-lg font-semibold">{p.name}</div>
                                <span className="text-xs inline-flex items-center gap-1 rounded-full px-2 py-0.5"
                                    style={{ background: p.is_active ? "var(--success-bg)" : "var(--input)",
                                              color: p.is_active ? "var(--success-fg)" : "var(--text-muted)" }}
                                    data-testid={`plan-status-${p.id || p.code}`}>
                                    {p.is_active ? <Check size={12} /> : <Power size={12} />}
                                    {p.is_active ? t("platform.plans.status_active") : t("platform.plans.status_inactive")}
                                </span>
                            </div>
                            <div className="text-2xl font-bold">{money(p.price_monthly_minor, p.currency, i18n.language)}<span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>/{t("plan.month")}</span></div>
                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("plan.annual")}: {money(p.price_annual_minor, p.currency, i18n.language)}</div>
                            {planDescription(p, i18n.language) && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{planDescription(p, i18n.language)}</div>}
                            <div className="mt-auto pt-2 flex items-center gap-2 flex-wrap">
                                <button data-testid={`plan-edit-${p.id || p.code}`} onClick={() => setEditPlanFor(p)}
                                    className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium"
                                    style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                                    <Edit3 size={12} />{t("platform.plans.edit_button")}
                                </button>
                                {p.is_active ? (
                                    <button data-testid={`plan-deactivate-${p.id || p.code}`} onClick={() => setDeactivatePlanFor(p)}
                                        className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs"
                                        style={{ background: "var(--input)", color: "var(--warning-fg)", borderColor: "var(--border)" }}>
                                        <Power size={12} />{t("platform.plans.deactivate_button")}
                                    </button>
                                ) : (
                                    <button data-testid={`plan-activate-${p.id || p.code}`} onClick={() => doPlanActivate(p)}
                                        className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs"
                                        style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>
                                        <Check size={12} />{t("platform.plans.activate_button")}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editPlanFor && <PlanEditDialog plan={editPlanFor} onClose={() => setEditPlanFor(null)} onSaved={() => { setEditPlanFor(null); load(); }} />}
            {deactivatePlanFor && (
                <ConfirmDialog
                    testId="plan-deactivate-dialog"
                    title={t("platform.plans.deactivate_confirm_title")}
                    desc={t("platform.plans.deactivate_confirm_desc", { plan: deactivatePlanFor.name })}
                    tone="warning"
                    confirmLabel={t("platform.plans.deactivate_confirm_yes")}
                    busy={deactivateBusy}
                    onCancel={() => setDeactivatePlanFor(null)}
                    onConfirm={() => doPlanDeactivate(deactivatePlanFor)}
                />
            )}
            {pricingFor && <TenantPriceOverrideDialog tenant={pricingFor} plans={plans} onClose={() => setPricingFor(null)} onChanged={() => load()} />}

            {editSubFor && <EditSubDialog tenant={editSubFor} plans={plans} onClose={() => setEditSubFor(null)} onSaved={() => { setEditSubFor(null); load(); }} />}
            {purgeFor && <PurgeTenantDialog tenant={purgeFor} onClose={() => setPurgeFor(null)} onDone={() => { setPurgeFor(null); load(); }} />}
            {deleteTenantFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="platform-delete-dialog">
                    <div className="rounded-xl p-5 max-w-md w-[90%]" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle size={18} style={{ color: "var(--error-fg)" }} />
                            <h2 className="text-lg font-semibold">{t("platform.tenants.delete_title")}</h2>
                        </div>
                        <div className="text-sm mb-2" style={{ color: "var(--text)" }}>
                            <span style={{ color: "var(--text-muted)" }}>{t("platform.tenants.delete_target")} </span>
                            <strong data-testid="delete-tenant-name">{deleteTenantFor.name}</strong>
                        </div>
                        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
                            {t("platform.tenants.delete_body")}
                        </p>
                        <div className="flex justify-end gap-2">
                            <button data-testid="platform-delete-cancel" disabled={deleteBusy} onClick={() => setDeleteTenantFor(null)}
                                className="rounded-md border px-3 py-1.5 text-sm" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                                {t("platform.tenants.delete_no")}
                            </button>
                            <button data-testid="platform-delete-confirm" disabled={deleteBusy}
                                onClick={async () => {
                                    if (!deleteTenantFor) return;
                                    setDeleteBusy(true);
                                    try {
                                        await api.delete(`/platform/tenants/${deleteTenantFor.id}`);
                                        toast.success(t("platform.tenants.delete_success"));
                                        setDeleteTenantFor(null);
                                        await load();
                                    } catch (e: any) {
                                        toast.error(formatApiError(e));
                                    } finally { setDeleteBusy(false); }
                                }}
                                className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium"
                                style={{ background: "var(--error-fg)", color: "#fff" }}>
                                {deleteBusy && <Loader2 size={14} className="animate-spin" />}
                                {t("platform.tenants.delete_yes")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {tab === "signups" && !loading && (
                <div className="space-y-3" data-testid="platform-signups">
                    <div className="flex gap-1 flex-wrap items-center">
                        <span className="text-xs mr-2" style={{ color: "var(--text-muted)" }}>{t("common.filter")}:</span>
                        {(["pending","approved","rejected","all"] as const).map(s => (
                            <button key={s} onClick={() => setSignupFilter(s)} data-testid={`signup-filter-${s}`}
                                className="rounded-md px-2.5 py-1 text-xs" style={{ background: signupFilter === s ? "var(--selected-bg)" : "var(--input)", color: "var(--text)" }}>
                                {t(`platform.signup.filter.${s}`)}
                            </button>
                        ))}
                    </div>
                    <div className="grid gap-3">
                        {signups.map(s => (
                            <div key={s.id} className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid={`signup-row-${s.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="flex-1 min-w-[220px]">
                                        <div className="flex items-center gap-2"><span className="font-semibold">{s.org_name}</span><SignupBadge status={s.status} t={t} /></div>
                                        <div className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{s.first_name} {s.last_name} · {s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
                                        <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                                            {t("platform.signup.activity")}: {(s.activity_types || []).map(a => t(`auth.signup.activity.${a}`, a)).join(", ")}
                                            {s.plan_interest ? ` · ${t("platform.signup.plan_interest")}: ${s.plan_interest}` : ""}
                                        </div>
                                        {s.message && <div className="text-sm mt-2 rounded-md p-2" style={{ background: "var(--input)" }}>{s.message}</div>}
                                        <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
                                            {t("platform.signup.received_at")}: {fmtDateTime(s.created_at)}
                                            {s.reviewed_at ? ` · ${t("platform.signup.reviewed_at")}: ${fmtDateTime(s.reviewed_at)}` : ""}
                                        </div>
                                        {s.reject_reason && <div className="text-xs mt-2 rounded-md p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{t("platform.signup.reject_reason")}: {s.reject_reason}</div>}
                                        {s.activation_url && (
                                            <div className="text-xs mt-2 rounded-md p-2" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }} data-testid={`signup-activation-${s.id}`}>
                                                <div className="font-semibold">{t("platform.signup.activation_link")}</div>
                                                <div className="mt-1 break-all">{s.activation_url}</div>
                                                <button onClick={() => copy(s.activation_url!)} className="mt-1 text-xs underline">{t("common.copy")}</button>
                                            </div>
                                        )}
                                        {s.status === "approved" && s.activation_used_at && <div className="text-xs mt-2" style={{ color: "var(--success-fg)" }}>{t("platform.signup.activation_used")}</div>}
                                    </div>
                                    {s.status === "pending" && (
                                        <div className="flex gap-2">
                                            <button onClick={() => approve(s)} data-testid={`signup-approve-${s.id}`} className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>{t("platform.signup.approve")}</button>
                                            <button onClick={() => { setRejectFor(s); setRejectReason(""); }} data-testid={`signup-reject-${s.id}`} className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{t("platform.signup.reject")}</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {signups.length === 0 && <div className="rounded-xl border p-6 text-center" style={{ borderColor: "var(--border)", background: "var(--card)", color: "var(--text-muted)" }} data-testid="signup-empty">{t("platform.signup.empty")}</div>}
                    </div>
                </div>
            )}

            {rejectFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
                    <div className="w-full max-w-sm rounded-xl border p-5 space-y-3" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="signup-reject-dialog">
                        <h2 className="text-lg font-semibold">{t("platform.signup.reject_title", { org: rejectFor.org_name })}</h2>
                        <textarea data-testid="signup-reject-reason" required minLength={2} maxLength={1000} rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                            placeholder={t("platform.signup.reject_reason_placeholder")} className="surface-input px-3 py-2 w-full text-sm" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setRejectFor(null); setRejectReason(""); }} className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("common.cancel")}</button>
                            <button onClick={doReject} disabled={rejectBusy} data-testid="signup-reject-confirm" className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{t("platform.signup.reject_confirm")}</button>
                        </div>
                    </div>
                </div>
            )}

            {approveFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
                    <div className="w-full max-w-4xl max-h-[90vh] rounded-xl border p-5 flex flex-col" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="signup-approve-dialog">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h2 className="text-lg font-semibold">{t("platform.access.approve_title")}</h2>
                                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t("platform.access.approve_desc")}</p>
                                <p className="text-xs mt-1" style={{ color: "var(--text)" }}>{approveFor.org_name} · {approveFor.email}</p>
                            </div>
                            <button onClick={() => setApproveFor(null)} data-testid="signup-approve-close"><X size={18} /></button>
                        </div>
                        <div className="overflow-y-auto flex-1">
                            <AccessPolicyEditor modules={accessCatalog} value={approvePolicy}
                                onChange={setApprovePolicy} testIdPrefix="approve-policy" />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setApproveFor(null)} className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("common.cancel")}</button>
                            <button onClick={doApprove}
                                disabled={approveBusy || !anyEnabledNonCritical(approvePolicy, accessCatalog)}
                                data-testid="signup-approve-confirm"
                                className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                                style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>
                                {approveBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                {t("platform.access.approve_btn")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Pagination({ page, setPage, total, pageSize, t }: { page: number; setPage: (n: number) => void; total: number; pageSize: number; t: any }) {
    const pages = Math.max(1, Math.ceil(total / pageSize));
    if (total <= pageSize) return null;
    return (
        <div className="flex items-center justify-between px-3 py-2 border-t text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
            <span>{t("common.showing", { from: page * pageSize + 1, to: Math.min(total, (page + 1) * pageSize), total })}</span>
            <div className="flex gap-1">
                <button data-testid="page-prev" disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded-md border px-2 py-1 disabled:opacity-40" style={{ borderColor: "var(--border)" }}>‹</button>
                <span className="px-2 py-1">{page + 1} / {pages}</span>
                <button data-testid="page-next" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="rounded-md border px-2 py-1 disabled:opacity-40" style={{ borderColor: "var(--border)" }}>›</button>
            </div>
        </div>
    );
}

function InvoiceStatus({ s, t }: { s: string; t: any }) {
    const map: Record<string, { bg: string; fg: string }> = {
        draft: { bg: "var(--input)", fg: "var(--text-muted)" },
        confirmed: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
        paid: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
        overdue: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
        void: { bg: "var(--error-bg)", fg: "var(--error-fg)" },
    };
    const c = map[s] || { bg: "var(--input)", fg: "var(--text)" };
    return <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: c.bg, color: c.fg }}>{t(`invoice.status.${s}`, s)}</span>;
}

function SignupBadge({ status, t }: { status: string; t: any }) {
    const map: Record<string, { bg: string; fg: string }> = {
        pending: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
        approved: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
        rejected: { bg: "var(--error-bg)", fg: "var(--error-fg)" },
    };
    const c = map[status] || { bg: "var(--input)", fg: "var(--text)" };
    return <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: c.bg, color: c.fg }} data-testid={`signup-status-${status}`}>{t(`platform.signup.status.${status}`)}</span>;
}

function MetricCard({ icon, label, value }: { icon: any; label: string; value: string }) {
    return (
        <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
            <div className="text-xs flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>{icon}{label}</div>
            <div className="text-2xl font-bold mt-1">{value}</div>
        </div>
    );
}

function StatusBadge({ status, t }: { status: string | undefined; t: any }) {
    if (!status) return <span style={{ color: "var(--text-muted)" }}>—</span>;
    const map: Record<string, { bg: string; fg: string }> = {
        active: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
        trial: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
        past_due: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
        suspended: { bg: "var(--error-bg)", fg: "var(--error-fg)" },
        cancelled: { bg: "var(--input)", fg: "var(--text-muted)" },
    };
    const c = map[status] || { bg: "var(--input)", fg: "var(--text)" };
    return <span className="text-xs rounded-full px-2 py-0.5" style={{ background: c.bg, color: c.fg }} data-testid={`sub-status-${status}`}>{t(`sub.status.${status}`, status)}</span>;
}

function EditSubDialog({ tenant, plans, onClose, onSaved }: { tenant: Tenant; plans: Plan[]; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const [planId, setPlanId] = useState(tenant.subscription?.plan?.id || plans[0]?.id || "");
    const [status, setStatus] = useState(tenant.subscription?.status || "trial");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try { await api.patch(`/platform/tenants/${tenant.id}/subscription`, { plan_id: planId, status }); onSaved(); }
        catch (e: any) { toast.error(formatApiError(e)); setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
            <div className="w-full max-w-sm rounded-xl border p-5 space-y-3" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="platform-edit-dialog">
                <div className="flex items-center justify-between"><h2 className="text-lg font-semibold">{tenant.name}</h2><button onClick={onClose}><X size={18} /></button></div>
                <label className="block text-sm">{t("platform.tenants.plan")}
                    <select data-testid="edit-plan" value={planId} onChange={e => setPlanId(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                        {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </label>
                <label className="block text-sm">{t("common.status")}
                    <select data-testid="edit-status" value={status} onChange={e => setStatus(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                        {["trial","active","past_due","suspended","cancelled"].map(s => <option key={s} value={s}>{t(`sub.status.${s}`, s)}</option>)}
                    </select>
                </label>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("common.cancel")}</button>
                    <button onClick={submit} disabled={busy} data-testid="edit-save" className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>{t("common.save")}</button>
                </div>
            </div>
        </div>
    );
}


// ---- Phase PKG-1: Plan editor dialog ---------------------------------------
// Full editor: name/description, monthly/annual price (AZN decimals ⇢ minor),
// currency, limits (blank = unlimited), modules, is_active, sort_order, and
// **apply_scope** with an extra confirm gate when switching to
// `existing_and_new` so a Platform Owner can never silently mutate every
// current subscription.
const ALL_MODULES = [
    "people","groups","schedule","attendance","finance",
    "crm","kindergarten","academics","hr","reports","procurement","inventory",
] as const;
const LIMIT_KEYS: readonly string[] = ["max_users","max_branches","max_active_students"];

function PlanEditDialog({ plan, onClose, onSaved }: { plan: Plan; onClose: () => void; onSaved: () => void }) {
    const { t, i18n } = useTranslation();
    const [name, setName] = useState(plan.name);
    const [descI18n, setDescI18n] = useState<Record<string, string>>({
        az: plan.description_i18n?.az || plan.description || "",
        en: plan.description_i18n?.en || "", ru: plan.description_i18n?.ru || "",
    });
    const [priceMonthlyAzn, setPriceMonthlyAzn] = useState(aznFromMinor(plan.price_monthly_minor));
    const [priceAnnualAzn, setPriceAnnualAzn] = useState(aznFromMinor(plan.price_annual_minor));
    const [currency, setCurrency] = useState(plan.currency || "AZN");
    const [sortOrder, setSortOrder] = useState(String(plan.sort_order ?? 100));
    const [isActive, setIsActive] = useState(plan.is_active !== false);
    const [modules, setModules] = useState<string[]>(plan.modules || []);
    // Limits use "" for null/unlimited so the input can be cleared.
    const initialLimits: Record<string, string> = {};
    for (const k of LIMIT_KEYS) {
        const v = (plan.limits || {})[k];
        initialLimits[k] = v == null ? "" : String(v);
    }
    const [limits, setLimits] = useState<Record<string, string>>(initialLimits);
    const [applyScope, setApplyScope] = useState<"new_only" | "existing_and_new">("new_only");
    const [confirmScope, setConfirmScope] = useState(false);
    const [busy, setBusy] = useState(false);

    const toggleModule = (m: string) => setModules(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

    const buildPayload = () => ({
        code: plan.code, name: name.trim(),
        description: descI18n.az.trim() || null,
        description_i18n: Object.fromEntries(Object.entries(descI18n).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v)),
        price_monthly_minor: minorFromAzn(priceMonthlyAzn),
        price_annual_minor: minorFromAzn(priceAnnualAzn),
        currency: currency.trim().toUpperCase() || "AZN",
        limits: Object.fromEntries(LIMIT_KEYS.map(k => {
            const raw = (limits[k] || "").trim();
            return [k, raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0)];
        })),
        modules,
        is_active: isActive,
        sort_order: Math.max(0, parseInt(sortOrder, 10) || 100),
        apply_scope: applyScope,
    });

    const submit = async () => {
        if (!plan.id) return;
        if (applyScope === "existing_and_new" && !confirmScope) {
            setConfirmScope(true); return;
        }
        setBusy(true);
        try {
            const r = await api.patch(`/platform/plans/${plan.id}`, buildPayload());
            toast.success(t("platform.plans.saved_toast"));
            const affected = Number(r.data?.affected_subscriptions || 0);
            if (affected > 0) toast.info(t("platform.plans.affected_toast", { count: affected }));
            onSaved();
        } catch (e: any) { toast.error(formatApiError(e)); setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" style={{ background: "rgba(0,0,0,0.6)" }}>
            <div className="w-full max-w-2xl max-h-[92vh] rounded-xl border flex flex-col" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="plan-edit-dialog">
                <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
                    <div>
                        <h2 className="text-lg font-semibold">{t("platform.plans.edit_title")}</h2>
                        <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{plan.name} · {plan.code}</p>
                    </div>
                    <button onClick={onClose} data-testid="plan-edit-close" aria-label="close"><X size={18} /></button>
                </div>
                <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
                    <div className="grid gap-3 md:grid-cols-2">
                        <label className="text-sm block">{t("platform.plans.field.name")}
                            <input data-testid="plan-name" value={name} onChange={e => setName(e.target.value)}
                                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                        </label>
                        <label className="text-sm block">{t("platform.plans.field.code")}
                            <input value={plan.code} readOnly
                                className="mt-1 w-full rounded-md border px-3 py-2 text-sm opacity-70"
                                style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text-muted)" }} />
                        </label>
                    </div>
                    <div className="grid gap-3 md:grid-cols-3">
                        {(["az", "en", "ru"] as const).map(lng => (
                            <label key={lng} className="text-sm block">{t("platform.plans.field.description")} ({lng.toUpperCase()})
                                <textarea data-testid={`plan-description-${lng}`} value={descI18n[lng]} rows={2}
                                    onChange={e => setDescI18n(prev => ({ ...prev, [lng]: e.target.value }))}
                                    className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                    style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                            </label>
                        ))}
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                        <label className="text-sm block">{t("platform.plans.field.price_monthly")}
                            <input data-testid="plan-price-monthly" inputMode="decimal" value={priceMonthlyAzn} onChange={e => setPriceMonthlyAzn(e.target.value)}
                                placeholder="50.00"
                                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                        </label>
                        <label className="text-sm block">{t("platform.plans.field.price_annual")}
                            <input data-testid="plan-price-annual" inputMode="decimal" value={priceAnnualAzn} onChange={e => setPriceAnnualAzn(e.target.value)}
                                placeholder="510.00"
                                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                        </label>
                        <label className="text-sm block">{t("platform.plans.field.currency")}
                            <select data-testid="plan-currency" value={currency} onChange={e => setCurrency(e.target.value)}
                                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                                {["AZN","USD","EUR","RUB","TRY"].map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                    </div>
                    <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("platform.plans.price_hint")}</p>

                    <div>
                        <div className="text-sm font-medium mb-2">{t("platform.plans.limits_title")}</div>
                        <div className="grid gap-3 md:grid-cols-3">
                            {LIMIT_KEYS.map(k => (
                                <label key={k} className="text-xs block">
                                    {t(`platform.plans.limit.${k}`)}
                                    <input data-testid={`plan-limit-${k}`} inputMode="numeric"
                                        value={limits[k as string] || ""} onChange={e => setLimits({ ...limits, [k as string]: e.target.value })}
                                        placeholder="∞" className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                        style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                                </label>
                            ))}
                        </div>
                        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{t("platform.plans.limit.unlimited_hint")}</p>
                    </div>

                    <div>
                        <div className="text-sm font-medium mb-2">{t("platform.plans.modules_title")}</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {ALL_MODULES.map(m => {
                                const on = modules.includes(m);
                                return (
                                    <label key={m} className="text-xs inline-flex items-center gap-2 rounded-md px-2 py-1.5 border cursor-pointer"
                                        data-testid={`plan-module-${m}`}
                                        style={{ borderColor: "var(--border)", background: on ? "var(--selected-bg)" : "var(--input)" }}>
                                        <input type="checkbox" checked={on} onChange={() => toggleModule(m)} />
                                        {t(`nav.${m}`, m)}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 items-end">
                        <label className="text-sm inline-flex items-center gap-2 mt-1">
                            <input type="checkbox" data-testid="plan-is-active" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                            {t("platform.plans.field.is_active")}
                        </label>
                        <label className="text-sm block">{t("platform.plans.field.sort_order")}
                            <input data-testid="plan-sort-order" inputMode="numeric" value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                        </label>
                    </div>

                    <div className="rounded-md border p-3" style={{ borderColor: "var(--border)", background: "var(--input)" }}>
                        <div className="text-sm font-medium mb-2">{t("platform.plans.apply_scope")}</div>
                        {(["new_only","existing_and_new"] as const).map(opt => (
                            <label key={opt} className="flex items-start gap-2 text-sm mb-2 last:mb-0 cursor-pointer"
                                data-testid={`plan-apply-scope-${opt}`}>
                                <input type="radio" name="apply-scope" checked={applyScope === opt} onChange={() => { setApplyScope(opt); setConfirmScope(false); }} />
                                <span>
                                    <span className="font-medium">{t(`platform.plans.apply_scope_${opt}`)}</span>
                                    <span className="block text-xs" style={{ color: "var(--text-muted)" }}>{t(`platform.plans.apply_scope_${opt}_hint`)}</span>
                                </span>
                            </label>
                        ))}
                        {applyScope === "existing_and_new" && (
                            <div className="mt-2 rounded-md p-2 text-xs inline-flex items-start gap-1.5" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="plan-apply-existing-warn">
                                <AlertTriangle size={12} className="mt-0.5" />
                                <span>{t("platform.plans.apply_existing_confirm_desc", { plan: plan.name })}</span>
                            </div>
                        )}
                    </div>
                    {money(minorFromAzn(priceMonthlyAzn), currency, i18n.language) && (
                        <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>= {money(minorFromAzn(priceMonthlyAzn), currency, i18n.language)} / {t("plan.month")}</div>
                    )}
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: "var(--border)" }}>
                    <button onClick={onClose} data-testid="plan-edit-cancel" className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("platform.plans.common.cancel")}</button>
                    <button onClick={submit} disabled={busy || !name.trim()} data-testid="plan-edit-save"
                        className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                        style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        {applyScope === "existing_and_new" && !confirmScope ? t("platform.plans.apply_existing_confirm_yes") : t("platform.plans.common.save")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- Phase PKG-1: Tenant custom pricing dialog -----------------------------
// Loads the current override (if any) via GET, lets the Platform Owner
// upsert it via PUT, and offers a *reset* (DELETE) button that soft-
// deactivates the override rather than hard-deleting it. Tenant subscriptions
// keep the record for auditability; future invoices fall back to plan default.
function TenantPriceOverrideDialog({ tenant, plans, onClose, onChanged }: { tenant: Tenant; plans: Plan[]; onClose: () => void; onChanged: () => void }) {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [confirmDeactivate, setConfirmDeactivate] = useState(false);
    const [override, setOverride] = useState<{
        plan_id: string; price_monthly_minor: number; price_annual_minor: number;
        currency: string; effective_from?: string | null; is_active: boolean;
    } | null>(null);
    const [planId, setPlanId] = useState<string>(tenant.subscription?.plan?.id || plans[0]?.id || "");
    const [priceMonthlyAzn, setPriceMonthlyAzn] = useState("0.00");
    const [priceAnnualAzn, setPriceAnnualAzn] = useState("0.00");
    const [currency, setCurrency] = useState("AZN");
    const [effectiveFrom, setEffectiveFrom] = useState<string>("");
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/platform/tenants/${tenant.id}/price-override`)
            .then(r => {
                const o = r.data?.override || null;
                setOverride(o);
                if (o) {
                    setPlanId(o.plan_id || planId);
                    setPriceMonthlyAzn(aznFromMinor(o.price_monthly_minor));
                    setPriceAnnualAzn(aznFromMinor(o.price_annual_minor));
                    setCurrency(o.currency || "AZN");
                    setEffectiveFrom(o.effective_from || "");
                    setIsActive(o.is_active !== false);
                } else {
                    // Prime the form with the plan's defaults so the Platform
                    // Owner sees what "no override" would bill.
                    const p = plans.find(x => x.id === planId);
                    if (p) { setPriceMonthlyAzn(aznFromMinor(p.price_monthly_minor)); setPriceAnnualAzn(aznFromMinor(p.price_annual_minor)); setCurrency(p.currency || "AZN"); }
                }
            })
            .catch(e => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenant.id]);

    const activePlan = plans.find(p => p.id === planId);

    const submit = async () => {
        setBusy(true);
        try {
            await api.put(`/platform/tenants/${tenant.id}/price-override`, {
                plan_id: planId,
                price_monthly_minor: minorFromAzn(priceMonthlyAzn),
                price_annual_minor: minorFromAzn(priceAnnualAzn),
                currency: currency.trim().toUpperCase() || "AZN",
                effective_from: effectiveFrom || null,
                is_active: isActive,
            });
            toast.success(t("platform.pricing.saved_toast"));
            onChanged();
            onClose();
        } catch (e: any) { toast.error(formatApiError(e)); setBusy(false); }
    };
    const deactivate = async () => {
        setBusy(true);
        try {
            await api.delete(`/platform/tenants/${tenant.id}/price-override`);
            toast.success(t("platform.pricing.deactivated_toast"));
            onChanged();
            onClose();
        } catch (e: any) { toast.error(formatApiError(e)); setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" style={{ background: "rgba(0,0,0,0.6)" }}>
            <div className="w-full max-w-lg max-h-[92vh] rounded-xl border flex flex-col" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="tenant-pricing-dialog">
                <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
                    <div>
                        <h2 className="text-lg font-semibold">{t("platform.pricing.edit_title", { tenant: tenant.name })}</h2>
                        <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t("platform.pricing.desc")}</p>
                    </div>
                    <button onClick={onClose} data-testid="tenant-pricing-close" aria-label="close"><X size={18} /></button>
                </div>
                <div className="overflow-y-auto px-5 py-4 space-y-3 flex-1">
                    {loading ? <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div> : (
                        <>
                            {!override && (
                                <div className="rounded-md p-2 text-xs" style={{ background: "var(--input)", color: "var(--text-muted)" }} data-testid="pricing-no-override">
                                    {t("platform.pricing.no_override_hint")}
                                </div>
                            )}
                            {override && (
                                <div className="rounded-md p-2 text-xs inline-flex items-center gap-1.5"
                                    style={{ background: override.is_active ? "var(--success-bg)" : "var(--warning-bg)",
                                              color: override.is_active ? "var(--success-fg)" : "var(--warning-fg)" }}
                                    data-testid={override.is_active ? "pricing-active-badge" : "pricing-inactive-badge"}>
                                    {override.is_active ? <Check size={12} /> : <Power size={12} />}
                                    {override.is_active ? t("platform.pricing.active_badge") : t("platform.pricing.inactive_badge")}
                                </div>
                            )}
                            <label className="text-sm block">{t("platform.pricing.plan_label")}
                                <select data-testid="pricing-plan" value={planId} onChange={e => setPlanId(e.target.value)}
                                    className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                    style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                                    {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </label>
                            {activePlan && (
                                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                                    {t("platform.pricing.default_hint", { price: money(activePlan.price_monthly_minor, activePlan.currency, i18n.language) })}
                                </div>
                            )}
                            <div className="grid gap-3 md:grid-cols-3">
                                <label className="text-sm block">{t("platform.plans.field.price_monthly")}
                                    <input data-testid="pricing-monthly" inputMode="decimal" value={priceMonthlyAzn} onChange={e => setPriceMonthlyAzn(e.target.value)}
                                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                        style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                                </label>
                                <label className="text-sm block">{t("platform.plans.field.price_annual")}
                                    <input data-testid="pricing-annual" inputMode="decimal" value={priceAnnualAzn} onChange={e => setPriceAnnualAzn(e.target.value)}
                                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                        style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                                </label>
                                <label className="text-sm block">{t("platform.plans.field.currency")}
                                    <select data-testid="pricing-currency" value={currency} onChange={e => setCurrency(e.target.value)}
                                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                        style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                                        {["AZN","USD","EUR","RUB","TRY"].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </label>
                            </div>
                            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("platform.plans.price_hint")}</p>
                            <label className="text-sm block">{t("platform.pricing.effective_from")}
                                <input type="date" data-testid="pricing-effective-from" value={effectiveFrom || ""} onChange={e => setEffectiveFrom(e.target.value)}
                                    className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                                    style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                            </label>
                            <label className="text-sm inline-flex items-center gap-2">
                                <input type="checkbox" data-testid="pricing-is-active" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                                {t("platform.pricing.is_active")}
                            </label>
                        </>
                    )}
                </div>
                <div className="flex justify-between gap-2 px-5 py-3 border-t" style={{ borderColor: "var(--border)" }}>
                    <div>
                        {override && override.is_active && (
                            <button onClick={() => setConfirmDeactivate(true)} disabled={busy} data-testid="pricing-deactivate"
                                className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-xs"
                                style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>
                                <Power size={12} />{t("platform.pricing.deactivate_button")}
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} data-testid="pricing-cancel" className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("platform.plans.common.cancel")}</button>
                        <button onClick={submit} disabled={busy || loading} data-testid="pricing-save"
                            className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                            style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            {t("platform.plans.common.save")}
                        </button>
                    </div>
                </div>
            </div>
            {confirmDeactivate && (
                <ConfirmDialog
                    testId="pricing-deactivate-dialog"
                    title={t("platform.pricing.deactivate_confirm_title")}
                    desc={t("platform.pricing.deactivate_confirm_desc", { tenant: tenant.name })}
                    tone="warning"
                    confirmLabel={t("platform.pricing.deactivate_confirm_yes")}
                    busy={busy}
                    onCancel={() => setConfirmDeactivate(false)}
                    onConfirm={deactivate}
                />
            )}
        </div>
    );
}

// ---- Reusable confirm dialog (Phase PKG-1) ---------------------------------
function ConfirmDialog({ title, desc, tone, confirmLabel, busy, onCancel, onConfirm, testId }: {
    title: string; desc: string; tone: "warning" | "danger"; confirmLabel: string;
    busy?: boolean; onCancel: () => void; onConfirm: () => void; testId: string;
}) {
    const { t } = useTranslation();
    const bg = tone === "danger" ? "var(--error-bg)" : "var(--warning-bg)";
    const fg = tone === "danger" ? "var(--error-fg)" : "var(--warning-fg)";
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.7)" }}>
            <div className="w-full max-w-sm rounded-xl border p-5 space-y-3" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid={testId}>
                <div className="inline-flex items-center gap-2">
                    <AlertTriangle size={16} style={{ color: fg }} />
                    <h2 className="text-base font-semibold">{title}</h2>
                </div>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>{desc}</p>
                <div className="flex justify-end gap-2 pt-1">
                    <button onClick={onCancel} className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid={`${testId}-cancel`}>{t("platform.plans.common.cancel")}</button>
                    <button onClick={onConfirm} disabled={busy} data-testid={`${testId}-confirm`}
                        className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
                        style={{ background: bg, color: fg }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}{confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TenantNameCell({ row }: { row: { id: string; tenant_name?: string | null; tenant_deleted?: boolean } }) {
    const { t } = useTranslation();
    return (
        <span className="inline-flex items-center gap-2 flex-wrap">
            {row.tenant_name || "—"}
            {row.tenant_deleted && (
                <span className="text-[10px] rounded-full px-1.5 py-0.5" data-testid={`tenant-deleted-badge-${row.id}`}
                    style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{t("platform.tenant_deleted_badge")}</span>
            )}
        </span>
    );
}
